import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Plus, Search, Trash2, Upload, Pencil, AlertTriangle, X, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

export default function Assets() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", status: "all", category: "all" });
  const [categories, setCategories] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const sortedAssets = useMemo(() => {
    if (!sort.key) return assets;
    const numericKeys = new Set(["purchase_cost"]);
    const isNumeric = numericKeys.has(sort.key);
    const arr = [...assets];
    arr.sort((a, b) => {
      let av = a[sort.key];
      let bv = b[sort.key];
      // Treat null/undefined/"" as empty; for numeric columns also treat 0 as empty
      // (missing prices default to 0 and render as "—").
      const aEmpty = av === null || av === undefined || av === "" || (isNumeric && !Number(av));
      const bEmpty = bv === null || bv === undefined || bv === "" || (isNumeric && !Number(bv));
      // Always push empty values to the bottom regardless of direction
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (isNumeric) {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
        return sort.dir === "asc" ? av - bv : bv - av;
      }
      // Natural, case-insensitive comparison so embedded numbers sort correctly
      // (e.g. "8GB" < "16GB", "TEST_A-2" < "TEST_A-10").
      const cmp = av.toString().localeCompare(bv.toString(), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [assets, sort]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.category !== "all") params.category = filters.category;
    try {
      const { data } = await api.get("/assets", { params });
      setAssets(data);
      // Drop any selected ids that are no longer in view
      setSelectedIds((prev) => {
        const next = new Set();
        const visible = new Set(data.map((a) => a.id));
        prev.forEach((id) => { if (visible.has(id)) next.add(id); });
        return next;
      });
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = assets.length > 0 && selectedIds.size === assets.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds((prev) => (
      prev.size === assets.length ? new Set() : new Set(assets.map((a) => a.id))
    ));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const onBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/assets/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} asset${data.deleted === 1 ? "" : "s"}`);
      setBulkDeleteOpen(false);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const onDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/assets/${deleteId}`);
      toast.success("Asset deleted");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const { data } = await api.post("/assets/import/csv", text, {
        headers: { "Content-Type": "text/csv" },
      });
      setImportResult({ ...data, filename: file.name });
      if (data.errors?.length) {
        toast.warning(`Imported with ${data.errors.length} row error${data.errors.length === 1 ? "" : "s"} — see details below`);
      } else {
        toast.success(`Imported · ${data.created} created · ${data.updated} updated`);
      }
      load();
    } catch (err) {
      toast.error("Import failed");
      setImportResult({ created: 0, updated: 0, errors: [{ row: "-", error: err.response?.data?.detail || err.message }], filename: file.name });
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div data-testid="assets-page">
      <PageHeader
        testid="assets-header"
        eyebrow="[02] Inventory"
        title="Assets"
        subtitle="Every tracked device, peripheral, and license. All monetary figures are NZD and GST exclusive."
        actions={
          <>
            <input ref={fileRef} type="file" accept=".csv" hidden onChange={onImport} data-testid="csv-import-input" />
            <Button
              variant="outline"
              data-testid="import-btn"
              onClick={() => fileRef.current?.click()}
              className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:text-[#0F0F0F] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase"
            >
              <Upload className="w-3.5 h-3.5 mr-2" />Import
            </Button>
            <a
              href={`${API}/assets/export/csv`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-btn"
              className="inline-flex items-center justify-center rounded-none border border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:text-[#0F0F0F] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase h-9 px-4 transition-colors"
            >
              <Download className="w-3.5 h-3.5 mr-2" />Export
            </a>
            <Button
              data-testid="new-asset-btn"
              onClick={() => navigate("/assets/new")}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
            >
              <Plus className="w-3.5 h-3.5 mr-2" />New Asset
            </Button>
          </>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {importResult && (
          <div data-testid="import-result-panel" className="border border-[#E5E0D5] bg-white">
            <div className="px-6 py-4 border-b border-[#E5E0D5] flex items-center justify-between">
              <div className="flex items-center gap-3">
                {importResult.errors?.length ? (
                  <AlertTriangle className="w-4 h-4 text-[#B45309]" strokeWidth={1.75} />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-[#2D7A4F]" />
                )}
                <div>
                  <div className="uppercase-label">Import Result · {importResult.filename}</div>
                  <div className="font-mono text-xs text-[#3A3A3A] mt-1">
                    <span className="text-[#2D7A4F]">{importResult.created} created</span>
                    <span className="text-[#9A9A9A] mx-2">·</span>
                    <span className="text-[#3A3A3A]">{importResult.updated} updated</span>
                    {importResult.errors?.length > 0 && (
                      <>
                        <span className="text-[#9A9A9A] mx-2">·</span>
                        <span className="text-[#B45309]">{importResult.errors.length} error{importResult.errors.length === 1 ? "" : "s"}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                data-testid="dismiss-import-result"
                onClick={() => setImportResult(null)}
                className="p-1.5 text-[#7A7A7A] hover:text-[#0F0F0F] transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E0D5]">
                      <th className="px-6 py-2 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] font-medium w-24">Row</th>
                      <th className="px-6 py-2 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.errors.map((err, i) => (
                      <tr key={i} data-testid={`import-error-${i}`} className="border-b border-[#E5E0D5]">
                        <td className="px-6 py-2 font-mono text-xs text-[#B45309]">{err.row}</td>
                        <td className="px-6 py-2 font-mono text-xs text-[#3A3A3A]">{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A7A]" />
            <Input
              data-testid="assets-search-input"
              placeholder="Search by tag, name, serial, manufacturer, assignee, CPU, RAM, location, Splashtop…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 rounded-none bg-white border-[#E5E0D5] font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status" className="rounded-none bg-white border-[#E5E0D5] font-mono text-xs uppercase tracking-wider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="in_use">In Use</SelectItem>
              <SelectItem value="storage">Storage</SelectItem>
              <SelectItem value="repair">Repair</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="filter-category" className="rounded-none bg-white border-[#E5E0D5] font-mono text-xs uppercase tracking-wider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk action toolbar */}
        {selectedIds.size > 0 && (
          <div data-testid="bulk-toolbar" className="flex items-center justify-between border border-[#E5E0D5] bg-[#1A1A1A] text-white px-4 py-2.5">
            <div className="flex items-center gap-3 font-mono text-xs">
              <span data-testid="bulk-count">{selectedIds.size} selected</span>
              <button
                onClick={clearSelection}
                data-testid="bulk-clear"
                className="text-[#B8B8B8] hover:text-white uppercase tracking-wider"
              >Clear</button>
            </div>
            <Button
              data-testid="bulk-delete-btn"
              onClick={() => setBulkDeleteOpen(true)}
              className="h-8 rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white font-mono text-xs tracking-wider uppercase"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete selected
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="assets-table">
            <thead>
              <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
                <th className="px-4 py-3 w-9">
                  <input
                    type="checkbox"
                    data-testid="select-all-checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    className="accent-[#F37B2A] cursor-pointer"
                  />
                </th>
                <Th sortKey="asset_tag" sort={sort} onSort={handleSort}>Tag</Th>
                <Th sortKey="name" sort={sort} onSort={handleSort}>Splashtop Name</Th>
                <Th sortKey="category" sort={sort} onSort={handleSort}>Category</Th>
                <Th sortKey="cpu" sort={sort} onSort={handleSort}>CPU</Th>
                <Th sortKey="ram" sort={sort} onSort={handleSort}>RAM</Th>
                <Th sortKey="status" sort={sort} onSort={handleSort}>Status</Th>
                <Th sortKey="assigned_to" sort={sort} onSort={handleSort}>Assigned</Th>
                <Th sortKey="location" sort={sort} onSort={handleSort}>Location</Th>
                <Th sortKey="purchase_cost" sort={sort} onSort={handleSort} align="right">Purchase Price</Th>
                <Th sortKey="purchase_date" sort={sort} onSort={handleSort} align="right">Purchase Date</Th>
                <Th sortKey="warranty_expiry" sort={sort} onSort={handleSort} align="right">Warranty</Th>
                <Th align="right"></Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={13} className="px-4 py-10 text-center font-mono text-xs text-[#7A7A7A]">Loading…</td></tr>
              )}
              {!loading && assets.length === 0 && (
                <tr><td colSpan={13} className="px-4 py-12 text-center">
                  <div className="font-mono text-xs text-[#7A7A7A] mb-3">No assets match the current filter.</div>
                  <Link to="/assets/new" className="font-mono text-xs uppercase tracking-wider text-[#1A1A1A]">+ Create one</Link>
                </td></tr>
              )}
              {sortedAssets.map((a) => (
                <tr key={a.id} data-testid={`asset-row-${a.id}`} className={`border-b border-[#E5E0D5] hover:bg-[#F0EDE3] transition-colors ${selectedIds.has(a.id) ? "bg-[#FFF4EE]" : ""}`}>
                  <td className="px-4 py-3 w-9">
                    <input
                      type="checkbox"
                      data-testid={`select-asset-${a.id}`}
                      aria-label={`Select ${a.asset_tag || a.name}`}
                      checked={selectedIds.has(a.id)}
                      onChange={() => toggleOne(a.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="accent-[#F37B2A] cursor-pointer"
                    />
                  </td>
                  <Td>
                    <Link to={`/assets/${a.id}`} data-testid={`asset-link-${a.id}`} className="font-mono text-xs text-[#0F0F0F] hover:text-[#1A1A1A]">{a.asset_tag || "—"}</Link>
                  </Td>
                  <Td>
                    <Link to={`/assets/${a.id}`} className="text-[#1A1A1A] whitespace-nowrap hover:text-[#1A1A1A]">{a.name}</Link>
                  </Td>
                  <Td><span className="font-mono text-xs text-[#5A5A5A]">{a.category}</span></Td>
                  <Td>
                    <span
                      className="font-mono text-xs text-[#5A5A5A] block max-w-[220px] truncate"
                      title={a.cpu || ""}
                      data-testid={`asset-cpu-${a.id}`}
                    >
                      {a.cpu || "—"}
                    </span>
                  </Td>
                  <Td>
                    <span
                      className="font-mono text-xs text-[#5A5A5A] whitespace-nowrap"
                      data-testid={`asset-ram-${a.id}`}
                    >
                      {a.ram || "—"}
                    </span>
                  </Td>
                  <Td><StatusPill status={a.status} data-testid={`asset-status-${a.id}`} /></Td>
                  <Td><span className="text-[#3A3A3A] whitespace-nowrap">{a.assigned_to || "—"}</span></Td>
                  <Td><span className="font-mono text-xs text-[#5A5A5A] whitespace-nowrap">{a.location || "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.purchase_cost ? `NZ$${Number(a.purchase_cost).toLocaleString()}` : "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#5A5A5A] whitespace-nowrap">{a.purchase_date || "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#5A5A5A] whitespace-nowrap">{a.warranty_expiry || "—"}</span></Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/assets/${a.id}/edit`)}
                        data-testid={`edit-asset-${a.id}`}
                        className="p-1.5 text-[#7A7A7A] hover:text-[#0F0F0F] transition-colors"
                        title="Edit"
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => setDeleteId(a.id)}
                        data-testid={`delete-asset-${a.id}`}
                        className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C] transition-colors"
                        title="Delete"
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="font-mono text-xs text-[#9A9A9A]">Total: {assets.length} {assets.length === 1 ? "asset" : "assets"}</div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">This action is permanent. The asset and its assignment history will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={onDelete} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-[#0F0F0F]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !o && setBulkDeleteOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="bulk-delete-title" className="font-medium tracking-tight">
              Delete {selectedIds.size} asset{selectedIds.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">
              This action is permanent. The selected assets and their assignment history will be removed for the current organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-bulk-delete-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-bulk-delete-btn"
              disabled={bulkDeleting}
              onClick={(e) => { e.preventDefault(); onBulkDelete(); }}
              className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white disabled:opacity-50"
            >{bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Th({ children, align = "left", sortKey, sort, onSort }) {
  const clickable = !!sortKey;
  const active = clickable && sort?.key === sortKey;
  return (
    <th
      className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-${align} font-medium ${
        clickable ? "cursor-pointer select-none hover:text-[#0F0F0F] transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[#F37B2A]" : ""
      } ${active ? "text-[#0F0F0F]" : "text-[#7A7A7A]"}`}
      onClick={clickable ? () => onSort(sortKey) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort(sortKey); } } : undefined}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? "button" : undefined}
      data-testid={clickable ? `sort-${sortKey}` : undefined}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {children}
        {clickable && (
          active
            ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
            : <ChevronsUpDown className="w-3 h-3 opacity-50" />
        )}
      </span>
    </th>
  );
}
function Td({ children, align = "left" }) {
  return <td className={`px-4 py-3 text-${align}`}>{children}</td>;
}
